import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import ProductCard from "@/components/store/ProductCard";

export default function Home() {
  const { content, categories, collections } = useStore();
  const [newArrivals, setNewArrivals] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);

  useEffect(() => {
    api.get("/products?is_new=true&limit=8").then((r) => setNewArrivals(r.data)).catch(() => {});
    api.get("/products?best_sellers=true&limit=4").then((r) => setBestSellers(r.data)).catch(() => {});
  }, []);

  const hero = content?.hero || {};
  const story = content?.story || {};
  const edit = content?.edit || {};
  const ig = content?.instagram || {};
  const photos = content?.customer_photos || [];
  const homeCats = categories.filter((c) => !["sale", "best-sellers"].includes(c.slug)).slice(0, 5);

  return (
    <div>
      {/* HERO */}
      <section className="relative h-[78vh] sm:h-[86vh] min-h-[520px] overflow-hidden bg-sharoa-ivory">
        <img src={hero.image} alt="SHAROA" className="absolute inset-0 w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-black/5 to-transparent" />
        <div className="relative h-full max-w-7xl mx-auto px-6 sm:px-10 flex flex-col justify-end pb-16 sm:pb-24">
          <div className="max-w-xl sh-fade-up">
            <p className="text-white/90 text-xs uppercase tracking-[0.3em] mb-4">SS'26 · New Season</p>
            <h1 className="font-serif text-white text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">{hero.headline}</h1>
            <p className="font-serif italic text-white text-2xl sm:text-4xl mt-2">{hero.subheadline}</p>
            <p className="text-white/90 text-sm sm:text-base mt-5 max-w-md">{hero.copy}</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/shop/new-arrivals" data-testid="hero-shop-new-cta" className="bg-white text-sharoa-charcoal text-xs uppercase tracking-[0.18em] px-8 py-4 hover:bg-sharoa-pale transition-colors">{hero.cta1 || "Shop New Arrivals"}</Link>
              <Link to="/collections" data-testid="hero-explore-cta" className="border border-white text-white text-xs uppercase tracking-[0.18em] px-8 py-4 hover:bg-white hover:text-sharoa-charcoal transition-colors">{hero.cta2 || "Explore Collection"}</Link>
            </div>
          </div>
        </div>
      </section>

      {/* NEW ARRIVALS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight">{content?.new_arrivals?.heading || "NEW ARRIVALS"}</h2>
            <p className="text-sharoa-muted mt-2 text-sm sm:text-base">{content?.new_arrivals?.subheading}</p>
          </div>
          <Link to="/shop/new-arrivals" className="hidden sm:flex items-center gap-2 text-xs uppercase tracking-widest sh-underline">View All <ArrowRight size={14} /></Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {newArrivals.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* SHOP BY CATEGORY */}
      <section className="bg-sharoa-pale py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-center mb-12">Shop by Category</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-5">
            {homeCats.map((c) => (
              <Link key={c.slug} to={`/shop/${c.slug}`} data-testid={`home-category-${c.slug}`} className="group relative aspect-[3/4] overflow-hidden sh-hover-zoom bg-sharoa-ivory">
                <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors" />
                <div className="absolute inset-0 flex items-end justify-center pb-6">
                  <span className="text-white font-serif text-lg sm:text-xl tracking-wide text-center px-2">{c.name}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-24 grid md:grid-cols-2 gap-10 items-center">
        <div className="aspect-[4/5] overflow-hidden bg-sharoa-ivory sh-hover-zoom order-1 md:order-none">
          <img src={story.image} alt="SHAROA story" className="w-full h-full object-cover" />
        </div>
        <div className="max-w-md">
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight leading-tight">{story.heading}</h2>
          <p className="text-sharoa-muted mt-5 leading-relaxed">{story.copy}</p>
          <Link to="/about" className="inline-block mt-7 border border-sharoa-rose text-sharoa-rose text-xs uppercase tracking-[0.18em] px-8 py-3.5 hover:bg-sharoa-rose hover:text-white transition-colors">{story.cta || "About SHAROA"}</Link>
        </div>
      </section>

      {/* THE SHAROA EDIT */}
      <section className="relative">
        <div className="grid md:grid-cols-2">
          <div className="relative aspect-square md:aspect-auto md:min-h-[560px] overflow-hidden bg-sharoa-ivory">
            <img src={edit.image1} alt="The SHAROA Edit" className="w-full h-full object-cover" />
          </div>
          <div className="relative bg-sharoa-charcoal text-white flex items-center justify-center py-20 px-8">
            <div className="max-w-sm text-center">
              <p className="text-xs uppercase tracking-[0.3em] text-white/60 mb-4">Editorial</p>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">{edit.heading || "THE SHAROA EDIT"}</h2>
              <p className="text-white/70 mt-5">Styled outfits and complete looks — shop each piece individually.</p>
              <Link to="/collections/the-sharoa-edit" className="inline-block mt-8 bg-white text-sharoa-charcoal text-xs uppercase tracking-[0.18em] px-8 py-4 hover:bg-sharoa-pale transition-colors">Shop the Look</Link>
            </div>
          </div>
        </div>
      </section>

      {/* BEST SELLERS */}
      {bestSellers.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-24">
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-center mb-3">Best Sellers</h2>
          <p className="text-sharoa-muted text-center mb-10 text-sm">Loved by SHAROA women everywhere ♡</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {bestSellers.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* SEEN ON YOU */}
      {photos.length > 0 && (
      <section data-testid="seen-on-you" className="bg-sharoa-ivory py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight">SEEN ON YOU ♡</h2>
          <p className="text-sharoa-muted mt-2">Tag @sharoaofficial for a chance to be featured ♡</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mt-10">
            {photos.map((src, i) => (
              <div key={i} className="aspect-square overflow-hidden sh-hover-zoom bg-white">
                <img src={src} alt="Customer" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* INSTAGRAM */}
      <section className="max-w-3xl mx-auto px-6 py-16 sm:py-24 text-center">
        <Instagram className="mx-auto text-sharoa-rose" size={34} />
        <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-5">{ig.heading || "FOLLOW SHAROA ♡"}</h2>
        <p className="text-sharoa-muted mt-3">{ig.copy}</p>
        <a href="https://instagram.com/sharoaofficial" target="_blank" rel="noreferrer" data-testid="home-instagram-cta"
          className="inline-block mt-7 bg-sharoa-rose text-white text-xs uppercase tracking-[0.18em] px-8 py-4 hover:bg-sharoa-rosedark transition-colors">Follow {ig.handle || "@sharoaofficial"}</a>
      </section>
    </div>
  );
}
